import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Branding, Copyright } from './Layout';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'AeMlnmkBe2UhQAi_9v6xw-zCko_C8tPi6OLl4d81eRh7nTskVR2zQveC3CIxENZ9Lal3IrnxJyhwJPRM';

const EventRegistration = ({ event, onBack, participant, onRegistered }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [cart, setCart] = useState({}); // { ticketId: quantity }
    const [otherParticipants, setOtherParticipants] = useState([]); // Array of { firstName, lastName, email, sex, age }
    const [emailExistsCache, setEmailExistsCache] = useState({});
    const [showPayPal, setShowPayPal] = useState(true);
    const [addedParticipants, setAddedParticipants] = useState([]);

    const isAlreadyRegistered = Boolean(
        event.isRegistered ||
        (participant.EventId && Number(participant.EventId) === Number(event.id)) ||
        (participant.Participations && participant.Participations.some(p => Number(p.EventId) === Number(event.id))) ||
        (participant.participations && participant.participations.some(p => Number(p.EventId) === Number(event.id)))
    );

    useEffect(() => {
        if (isAlreadyRegistered && participant.UserId) {
            axios.get(`/speeddate/api/participants/added-by/${participant.UserId}?eventId=${event.id}`)
                .then(res => setAddedParticipants(res.data))
                .catch(err => console.error(err));
            setLoading(false);
        } else {
            fetchTickets();
        }
    }, [event.id]);

    const fetchTickets = async () => {
        try {
            const res = await axios.get(`/speeddate/api/tickets?eventId=${event.id}`);
            const activeTickets = res.data.filter(t => t.active);
            
            // Se NON è già registrato a questo evento, filtra per sesso
            const alreadyRegistered = Boolean(
                event.isRegistered ||
                (participant.EventId && Number(participant.EventId) === Number(event.id)) ||
                (participant.Participations && participant.Participations.some(p => Number(p.EventId) === Number(event.id))) ||
                (participant.participations && participant.participations.some(p => Number(p.EventId) === Number(event.id)))
            );
            
            if (!alreadyRegistered) {
                const isMale = participant.sex === 'M' || participant.sex === 'male';
                setTickets(activeTickets.filter(t => isMale ? t.maleCount > 0 : t.femaleCount > 0));
            } else {
                // Già registrato: può aggiungere qualsiasi biglietto (amici/coppie)
                setTickets(activeTickets);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const updateQuantity = (id, delta) => {
        setCart(prev => {
            const current = prev[id] || 0;
            const next = Math.max(0, current + delta);
            return { ...prev, [id]: next };
        });
        // PayPal always shown
    };

    // Calculations
    const total = tickets.reduce((sum, t) => sum + (t.price * (cart[t.id] || 0)), 0);
    const totalItems = Object.values(cart).reduce((a, b) => a + b, 0);
    const hasTicketsSelected = totalItems > 0;

    const totalMales = tickets.reduce((sum, t) => sum + (t.maleCount * (cart[t.id] || 0)), 0);
    const totalFemales = tickets.reduce((sum, t) => sum + (t.femaleCount * (cart[t.id] || 0)), 0);

    // Validation
    const isMale = participant.sex === 'M' || participant.sex === 'male';
    const isFemale = participant.sex === 'F' || participant.sex === 'female';
    const hasOwnSex = isAlreadyRegistered || (isMale && totalMales >= 1) || (isFemale && totalFemales >= 1);

    const reqMales = Math.max(0, totalMales - (isMale && !isAlreadyRegistered ? 1 : 0));
    const reqFemales = Math.max(0, totalFemales - (isFemale && !isAlreadyRegistered ? 1 : 0));
    const totalOtherNeeded = reqMales + reqFemales;

    // Sync otherParticipants array when cart changes
    useEffect(() => {
        const currentList = [...otherParticipants];
        const newList = [];

        for (let i = 0; i < reqMales; i++) {
            const existing = currentList.find((p, idx) => p.sex === 'M' && !newList.some(n => n.originalIdx === idx));
            newList.push(existing ? { ...existing, originalIdx: currentList.indexOf(existing) } : { firstName: '', lastName: '', email: '', sex: 'M', age: '' });
        }
        for (let i = 0; i < reqFemales; i++) {
            const existing = currentList.find((p, idx) => p.sex === 'F' && !newList.some(n => n.originalIdx === idx));
            newList.push(existing ? { ...existing, originalIdx: currentList.indexOf(existing) } : { firstName: '', lastName: '', email: '', sex: 'F', age: '' });
        }

        setOtherParticipants(newList.map(({ originalIdx, ...rest }) => rest));
    }, [reqMales, reqFemales]);

    const updateOtherParticipant = (idx, field, value) => {
        const newList = [...otherParticipants];
        newList[idx][field] = value;
        setOtherParticipants(newList);
    };

    const handleEmailBlur = async (email) => {
        if (!email) return;
        const normalized = email.toLowerCase().trim();
        if (emailExistsCache[normalized] !== undefined) return;
        try {
            const res = await axios.get(`/speeddate/api/users/check-email?email=${encodeURIComponent(normalized)}`);
            setEmailExistsCache(prev => ({ ...prev, [normalized]: res.data.exists }));
        } catch (err) {
            console.error(err);
        }
    };

    // Email Uniqueness Check
    const allEmails = [participant.email, ...otherParticipants.map(p => p.email)].filter(e => !!e);
    const hasDuplicateEmails = new Set(allEmails).size !== allEmails.length;

    const isFormValid = totalItems > 0 &&
        hasOwnSex &&
        !hasDuplicateEmails &&
        totalOtherNeeded === otherParticipants.filter(p => {
            const hasBasic = p.firstName && p.email;
            const emailNormalized = p.email ? p.email.toLowerCase().trim() : '';
            const emailStatus = emailExistsCache[emailNormalized];
            const needsAge = emailStatus === false;
            if (needsAge) return hasBasic && p.age;
            return hasBasic;
        }).length;

    // PayPal handlers
    const createOrder = async () => {
        const res = await axios.post('/speeddate/api/payments/paypal/create-order', {
            amount: total.toFixed(2),
            currency: 'EUR',
            description: `Speed Date: ${event.name}`
        });
        return res.data.id;
    };

    const onApprove = async (data) => {
        try {
            setIsProcessing(true);
            const res = await axios.post('/speeddate/api/payments/paypal/capture-order', {
                orderId: data.orderID,
                eventId: event.id,
                participantId: participant.UserId || participant.id,
                otherParticipants
            });

            if (res.data.success) {
                await window.Swal.fire({
                    title: isAlreadyRegistered ? 'Amici Aggiunti!' : 'Pagamento Completato!',
                    text: isAlreadyRegistered
                        ? 'I tuoi amici sono stati registrati con successo.'
                        : 'Il pagamento è andato a buon fine. Ti aspettiamo all\'evento!',
                    icon: 'success',
                    confirmButtonColor: '#db2777'
                });
                if (onRegistered) onRegistered(res.data.participant);
            }
        } catch (err) {
            const msg = err.response?.data?.error || 'Si è verificato un problema durante il pagamento.';
            window.Swal.fire('Errore', msg, 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const onError = (err) => {
        console.error('PayPal error:', err);
        window.Swal.fire('Errore PayPal', 'Si è verificato un problema con PayPal. Riprova.', 'error');
    };

    // Free events (total == 0): register directly without PayPal
    const handleFreeRegistration = async () => {
        if (!isFormValid) return;
        try {
            setIsProcessing(true);
            const res = await axios.post('/speeddate/api/users/register-to-event', {
                eventId: event.id,
                participantId: participant.UserId || participant.id,
                otherParticipants
            });
            if (res.data.success) {
                await window.Swal.fire({
                    title: isAlreadyRegistered ? 'Amici Aggiunti!' : 'Iscrizione Completata!',
                    text: 'Sei stato registrato con successo!',
                    icon: 'success',
                    confirmButtonColor: '#db2777'
                });
                if (onRegistered) onRegistered(res.data.participant);
            }
        } catch (err) {
            const msg = err.response?.data?.error || 'Si è verificato un problema durante l\'iscrizione.';
            window.Swal.fire('Errore', msg, 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="mx-auto w-100 fade-in" style={{ maxWidth: '800px' }}>
            <div className="card glass-premium border-0 p-0 mb-4 shadow-lg overflow-hidden" style={{ borderRadius: '45px' }}>
                {/* Header */}
                <div className="p-4 p-md-5 bg-white border-bottom position-relative">
                    <button
                        className="btn btn-light rounded-circle shadow-sm position-absolute"
                        style={{ top: '25px', left: '25px', width: '45px', height: '45px', zIndex: 10 }}
                        onClick={onBack}
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>

                    <div className="text-center mt-3">
                        <Branding />
                        <h1 className="fw-900 text-dark mt-4 mb-2" style={{ fontSize: '2.5rem' }}>{event.name}</h1>
                        <div className="d-flex justify-content-center gap-3 text-pink-600 fw-700 small text-uppercase ls-md">
                            <span><i className="fas fa-calendar-day me-2"></i>{new Date(event.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            <span><i className="fas fa-clock me-2"></i>{event.startTime?.substring(0, 5)} - {event.endTime?.substring(0, 5)}</span>
                        </div>
                        {isAlreadyRegistered && (
                            <div className="badge bg-success text-white p-2 px-4 rounded-pill fw-900 fs-6 shadow-sm mt-3 animate-pulse">
                                <i className="fas fa-check-circle me-2"></i> SEI ISCRITTO
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-4 p-md-5">
                    <div className="row g-5">
                        {/* Event Info */}
                        <div className="col-lg-7">
                            <h4 className="fw-900 text-dark mb-4 border-bottom pb-2">Informazioni Evento</h4>
                            <div className="mb-4">
                                <p className="fw-700 text-muted mb-1 small text-uppercase ls-sm">Location</p>
                                <p className="fw-900 text-dark h5 mb-3"><i className="fas fa-map-marker-alt text-pink-600 me-2"></i>{event.location} - {event.city?.name}</p>
                                {isAlreadyRegistered && (
                                    <div className="badge bg-success text-white p-2 px-3 rounded-pill fw-900 fs-6 shadow-sm mt-1">
                                        <i className="fas fa-check-circle me-2"></i> TICKET PERSONALE ACQUISTATO
                                    </div>
                                )}
                            </div>
                            <div className="event-description-container bg-light p-4 rounded-4">
                                <p className="fw-700 text-muted mb-3 small text-uppercase ls-sm">Descrizione</p>
                                <div
                                    className="text-dark fw-600 small"
                                    style={{ lineHeight: '1.6' }}
                                    dangerouslySetInnerHTML={{ __html: (event.description || 'Nessuna descrizione disponibile.').replace(/\n/g, '<br>') }}
                                />
                            </div>
                        </div>

                        {/* Checkout & PayPal */}
                        <div className="col-lg-5">
                            {isAlreadyRegistered ? (
                                <div className="card bg-white border-0 shadow-sm rounded-5 overflow-hidden">
                                    <div className="bg-success p-4 text-white text-center">
                                        <h5 className="fw-900 mb-0 text-uppercase ls-lg"><i className="fas fa-check-circle me-2"></i>Sei Iscritto!</h5>
                                    </div>
                                    <div className="p-4">
                                        <div className="bg-light p-4 rounded-4 mb-4">
                                            <p className="fw-900 text-dark mb-1 h6">{participant.firstName} {participant.lastName}</p>
                                            <p className="text-muted small mb-0">{participant.email}</p>
                                            <div className="mt-2">
                                                <span className={`badge ${isMale ? 'bg-primary' : 'bg-pink-600'} rounded-pill small`}>
                                                    {isMale ? 'Uomo' : 'Donna'}
                                                </span>
                                            </div>
                                        </div>

                                        {addedParticipants.length > 0 && (
                                            <div className="border-top pt-4">
                                                <h6 className="fw-900 text-dark mb-3 text-uppercase ls-sm">
                                                    <i className="fas fa-users me-2 text-muted"></i>Altri Partecipanti
                                                </h6>
                                                {addedParticipants.map((p, idx) => (
                                                    <div key={idx} className="bg-light p-3 rounded-4 mb-3 border-start border-4" style={{ borderColor: p.sex === 'male' ? '#3b82f6' : '#db2777' }}>
                                                        <p className="fw-900 small mb-1 text-dark">{p.firstName} {p.lastName}</p>
                                                        <p className="text-muted small mb-0">{p.email}</p>
                                                        <span className={`badge mt-2 ${p.sex === 'male' ? 'bg-primary-subtle text-primary' : 'bg-danger-subtle text-danger'} rounded-pill`} style={{ fontSize: '10px' }}>
                                                            {p.sex === 'male' ? 'Uomo' : 'Donna'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                            <div className="card bg-white border-0 shadow-sm rounded-5 overflow-hidden">
                                <div className="bg-pink-600 p-4 text-white text-center">
                                    <h5 className="fw-900 mb-0 text-uppercase ls-lg">Il Tuo Carrello</h5>
                                </div>
                                <div className="p-4">
                                    {loading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-pink-600" role="status"></div>
                                        </div>
                                    ) : (
                                        <div className="ticket-list mb-4">
                                            {tickets.map(t => (
                                                <div key={t.id} className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light last-child-border-0">
                                                    <div className="flex-grow-1">
                                                        <p className="fw-900 text-dark mb-0">{t.name}</p>
                                                        <p className="small mb-0 d-flex align-items-center gap-2">
                                                            {t.fullprice && parseFloat(t.fullprice) > 0 && (
                                                                <span className="text-muted text-decoration-line-through" style={{ fontSize: '11px' }}>€{parseFloat(t.fullprice).toFixed(2)}</span>
                                                            )}
                                                            <span className="text-pink-600 fw-700">€{parseFloat(t.price).toFixed(2)}</span>
                                                            {t.fullprice && parseFloat(t.fullprice) > 0 && (
                                                                <span className="badge rounded-pill text-white" style={{ background: '#ec4899', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 800 }}>Promo!</span>
                                                            )}
                                                        </p>
                                                        <div className="mt-1 d-flex gap-1">
                                                            {t.maleCount > 0 && <span className="badge bg-primary-subtle text-primary rounded-pill" style={{ fontSize: '9px' }}>{t.maleCount}M</span>}
                                                            {t.femaleCount > 0 && <span className="badge bg-danger-subtle text-danger rounded-pill" style={{ fontSize: '9px' }}>{t.femaleCount}F</span>}
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center bg-light rounded-pill p-1 shadow-inner">
                                                        <button
                                                            className="btn btn-sm btn-white rounded-circle shadow-sm"
                                                            style={{ width: '30px', height: '30px' }}
                                                            onClick={() => updateQuantity(t.id, -1)}
                                                        >
                                                            <i className="fas fa-minus small text-muted"></i>
                                                        </button>
                                                        <span className="px-3 fw-900 text-dark" style={{ minWidth: '40px', textAlign: 'center' }}>{cart[t.id] || 0}</span>
                                                        <button
                                                            className="btn btn-sm btn-white rounded-circle shadow-sm"
                                                            style={{ width: '30px', height: '30px' }}
                                                            onClick={() => updateQuantity(t.id, 1)}
                                                        >
                                                            <i className="fas fa-plus small text-muted"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Additional Participants */}
                                    {otherParticipants.length > 0 && (
                                        <div className="mt-4 pt-4 border-top">
                                            <h6 className="fw-900 text-dark mb-3 text-uppercase ls-sm">Dati Altri Invitati</h6>
                                            {otherParticipants.map((p, idx) => (
                                                <div key={idx} className="bg-light p-3 rounded-4 mb-3 border-start border-4" style={{ borderColor: p.sex === 'M' ? '#3b82f6' : '#db2777' }}>
                                                    <p className="fw-900 small mb-2 text-dark opacity-75">{p.sex === 'M' ? 'UOMO' : 'DONNA'} AGGIUNTIVO</p>
                                                    <div className="row g-2">
                                                        <div className="col-6">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm rounded-pill"
                                                                placeholder="Nome*"
                                                                value={p.firstName}
                                                                onChange={(e) => updateOtherParticipant(idx, 'firstName', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-6">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm rounded-pill"
                                                                placeholder="Cognome"
                                                                value={p.lastName}
                                                                onChange={(e) => updateOtherParticipant(idx, 'lastName', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-12">
                                                            <input
                                                                type="email"
                                                                className="form-control form-control-sm rounded-pill"
                                                                placeholder="Email*"
                                                                value={p.email}
                                                                onChange={(e) => updateOtherParticipant(idx, 'email', e.target.value)}
                                                                onBlur={() => handleEmailBlur(p.email)}
                                                            />
                                                        </div>
                                                        {emailExistsCache[p.email?.toLowerCase().trim()] === false && (
                                                            <div className="col-12 mt-1">
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm rounded-pill"
                                                                    placeholder="Età*"
                                                                    value={p.age || ''}
                                                                    onChange={(e) => updateOtherParticipant(idx, 'age', e.target.value)}
                                                                    min="18"
                                                                    max="99"
                                                                />
                                                                <small className="text-muted ms-2" style={{ fontSize: '11px' }}>
                                                                    Richiesto per i nuovi utenti
                                                                </small>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Summary */}
                                    <div className="bg-light p-4 rounded-4 mt-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted fw-700 small">Riepilogo:</span>
                                            <div className="d-flex gap-2">
                                                <span className="badge bg-primary rounded-pill small">{totalMales}M</span>
                                                <span className="badge bg-pink-600 rounded-pill small">{totalFemales}F</span>
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top border-2 border-white">
                                            <span className="h5 fw-900 text-dark mb-0">TOTALE:</span>
                                            <span className="h4 fw-900 text-pink-600 mb-0">€{total.toFixed(2)}</span>
                                        </div>
                                    </div>

                                    {/* Validation errors */}
                                    {!hasOwnSex && totalItems > 0 && (
                                        <div className="alert alert-warning border-0 rounded-4 mt-3 small fw-700 py-2">
                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                            Devi includere te stesso ({isMale ? 'Uomo' : 'Donna'}).
                                        </div>
                                    )}
                                    {hasDuplicateEmails && (
                                        <div className="alert alert-danger border-0 rounded-4 mt-3 small fw-700 py-2">
                                            <i className="fas fa-envelope me-2"></i>
                                            Email duplicate non ammesse.
                                        </div>
                                    )}

                                    {/* Payment Button */}
                                    {total > 0 ? (
                                        <div className="mt-4">
                                            <p className="text-center text-muted small fw-700 mb-3">
                                                <i className="fab fa-paypal me-2 text-primary"></i>
                                                Paga in modo sicuro con PayPal
                                            </p>
                                            <PayPalScriptProvider options={{
                                                'client-id': PAYPAL_CLIENT_ID,
                                                currency: 'EUR',
                                                locale: 'it_IT'
                                            }}>
                                                <PayPalButtons
                                                    style={{ layout: 'vertical', shape: 'pill', label: 'pay' }}
                                                    disabled={isProcessing || !isFormValid}
                                                    createOrder={createOrder}
                                                    onApprove={onApprove}
                                                    onError={onError}
                                                />
                                            </PayPalScriptProvider>
                                        </div>
                                    ) : hasTicketsSelected ? (
                                        <button
                                            className="btn btn-danger w-100 rounded-pill py-3 mt-4 fw-900 shadow-lg border-0 transition-all hover-scale"
                                            style={{ background: 'linear-gradient(45deg, #db2777, #f43f5e)' }}
                                            disabled={!isFormValid || isProcessing}
                                            onClick={handleFreeRegistration}
                                        >
                                            {isProcessing ? <i className="fas fa-spinner fa-spin"></i> : <>ISCRIVITI GRATUITAMENTE <i className="fas fa-chevron-right ms-2"></i></>}
                                        </button>
                                    ) : (
                                        <div className="text-center mt-4">
                                            <div className="d-inline-block bg-danger bg-opacity-10 text-danger border border-danger rounded-4 px-4 py-3 small fw-600" style={{ whiteSpace: 'normal', maxWidth: '220px', lineHeight: '1.5' }}>
                                                <i className="fas fa-exclamation-circle me-2"></i>
                                                Seleziona almeno un biglietto<br />per procedere
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Copyright />
        </div>
    );
};

export default EventRegistration;
